import { useEffect } from 'react';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { logEvent } from "../utils/analytics";

import API from '../utils/api';
import Search from '../components/Search';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import EmptyMap from '../assets/img/empty-map.png';
import RegisterForm, { USER_REGISTRATION_KEY } from '../components/forms/RegisterForm';
import SubscribeForm from '../components/forms/SubscribeForm';
import CandidatesTab from '../components/election/CandidatesTab';
import WhereToVoteForm from '../components/forms/WhereToVoteForm';
import Layout from '../components/layout/Layout';

const DEFAULT_LOCATION = {
    lat: '6.5196371',
    lng: '3.393482',
    street: 'University of Lagos'
}

const ResultsPage = ({ year, dummy = false }) => {
    const [isUserVerified, setisUserVerified] = useState(!!localStorage.getItem(USER_REGISTRATION_KEY));

    const [state, setState] = useState('pending');
    const [activeTab, setActiveTab] = useState('candidates');
    const [showDrawer, setShowDrawer] = useState(!isUserVerified);
    const [location, setLocation] = useState({ lat: '', lng: '', street: '' });
    const [electionData, setElectionData] = useState({});
    const [useFallbackMap, setUseFallbackMap] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();

    useEffect(() => {
        const queryTab = searchParams.get('tab')
        setActiveTab(queryTab || 'candidates');

        validateCoordinates();
    }, [searchParams]);

    useEffect(() => {
        if (isUserVerified) return;

        setShowDrawer(true);

    }, []);

    const getStreetAddress = (lat, lng) => {
        setState('loading');
        API.reverseGeocode(lat, lng).then((res) => {
            updatePartialSearchParams({ street: res?.data?.data?.formattedAddress });
        })
            .catch(() => {
                setState('error');
            });
    };


    const updatePartialSearchParams = ({ tab, position, street }) => {
        setSearchParams({
            position: position || searchParams.get('position'),
            street: street || searchParams.get('street'),
            tab: tab || searchParams.get('tab') || activeTab,
        });
    }

    const validateCoordinates = () => {
        const position = searchParams.get('position');
        const street = searchParams.get('street');
        const defaultParams = {
            position: `${DEFAULT_LOCATION.lat},${DEFAULT_LOCATION.lng}`,
            street: DEFAULT_LOCATION.street
        };

        if (!position) {
            updatePartialSearchParams(defaultParams);
            return;
        }

        const coordinates = position.split(',');

        if (coordinates.length !== 2) {
            updatePartialSearchParams(defaultParams);
            return;
        }

        const [lat, lng] = coordinates;

        if (!street) {
            getStreetAddress(lat, lng);
            return;
        }

        fetchCandidates({ lat, lng, street });
    };

    const fetchCandidates = ({ lat, lng, street }) => {

        if(location.lat === lat && location.lng === lng) return;
        setLocation({ lat, lng, street });
        setState('loading');

        // @TODO: Remove this condition
        const apiFunction = year
            ? API.fetchCandidatesByPosition(lat, lng, year)
            : API.fetchCandidatesByPositionForUpcomingElection({ lat, lng, dummy });

        apiFunction.then((res) => {
            const data = res?.data?.data;

            setElectionData(data);

            setState('success');

        }).catch(() => {
            setState('error');
        });
    };

    const switchTab = (tab) => {
        logEvent('Results page', 'Switch to tab', tab);

        updatePartialSearchParams({ tab })
    }

    const onGetNotifiedClick = () => {
        logEvent('Results page', 'Clicked Register Button', 'Get Notified');
        setShowDrawer(true)
    }

    const onRegister = () => {
        setShowDrawer(false);
        setisUserVerified(true);
    }

    const locationPoint = `${location.lat},${location.lng}`;
    const locationKey = `${location.lat},${location.lng},${location.street}`;
    const mapImageSrc = (useFallbackMap || !location.lat || !location.lng) ? EmptyMap : `https://maps.googleapis.com/maps/api/staticmap?bottom=${locationPoint}&size=700x350&zoom=16&markers=color:red%7C${locationPoint}&key=AIzaSyAfH6RriIeqgnV9VAt_FXvA0P%20-%20aKLwM_s0`;

    return (
        <Layout>
            <Header />

            <div className="space-y-6 ">

                <div className="relative flex flex-col min-h-screen">

                    <section className="relative md:w-[700px] h-[220px] md:h-[320px]">
                        <div className="absolute top-3 inset-x-4">
                            <Search key={locationKey} queryLocation={location} loading={state === 'loading'} />
                        </div>
                        <img
                            key={locationPoint}
                            className="md:rounded-2xl shadow inline-block w-full "
                            src={mapImageSrc}
                            alt={location.street}
                            onError={() => setUseFallbackMap(true)}
                        />
                    </section>

                    <section
                        className="grow z-10 bg-white flex [!]md:flex-row flex-col w-full border shadow-lg rounded-xl">
                        <div className="flex justify-between border-b border-slate-100">
                            <button onClick={() => switchTab('candidates')} className=" grow text-center">
                                <h4 className={`${activeTab === 'candidates' ? 'font-bold text-slate-900' : 'text-slate-700'} p-4`}>Candidates Info</h4>
                                <div className={`${activeTab === 'candidates' ? 'bg-slate-900' : 'font-bold bg-transparent'} h-0.5 rounded-t`}></div>
                            </button>
                            <button onClick={() => switchTab('where-to-vote')} className=" grow text-center">
                                <h4 className={`${activeTab === 'where-to-vote' ? 'font-bold text-slate-900' : 'text-slate-700'} p-4`}>Where to Vote</h4>
                                <div className={`${activeTab === 'where-to-vote' ? 'bg-slate-900' : 'font-bold bg-transparent'} h-0.5 rounded-t`}></div>
                            </button>
                        </div>

                        <div className={activeTab === 'candidates' ? 'block' : 'hidden'}>
                            <CandidatesTab state={state} electionData={electionData} isUserVerified={isUserVerified} onGetNotifiedClick={onGetNotifiedClick} />
                        </div>
                        <div className={activeTab === 'where-to-vote' ? 'block' : 'hidden'}>
                             <WhereToVoteForm />
                        </div>
                    </section>

                    <section
                        className={`${showDrawer ? 'opacity-1 inset-x-0 inset-y-0 md:w-[700px]' : 'h-0 w-0 opacity-0'} fixed mx-auto flex items-end z-10 transition-opacity backdrop-blur-sm ease-out bg-black/75`}>
                        <div
                            className={`${showDrawer ? 'md:h-[410px] h-[480px] opacity-1' : 'h-0 opacity-0'} transition-all duration-500 w-full bg-white rounded-t-3xl shadow`}>
                            <RegisterForm onClose={() => setShowDrawer(false)} onRegister={onRegister} />
                        </div>
                    </section>
                </div>

                <SubscribeForm />

                <Footer />
            </div>
        </Layout>
    );
};

export default ResultsPage;
